import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  Users,
  Calendar,
  Utensils,
  ClipboardList,
  AlertTriangle,
  Copy as CopyIcon,
} from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { PianoWeekTab } from '../components/PianoWeekTab';
import {
  usePatients,
  useCreateDietPlan,
  useCloneDietPlan,
  useDietPlan,
  useUpdateDietPlan,
  useCreatePlanTemplate,
} from '../lib/queries';
import {
  ApiError,
  DAY_LABELS,
  type PlanEditorMeal,
  type MealType,
  type DietPlanDetail,
} from '../lib/api';
import styles from './Dashboard.module.css';

type Step = 1 | 2 | 3 | 4;

const STEPS: Array<{ n: Step; label: string; icon: typeof Users }> = [
  { n: 1, label: 'Cliente', icon: Users },
  { n: 2, label: 'Durata', icon: Calendar },
  { n: 3, label: 'Pasti', icon: Utensils },
  { n: 4, label: 'Revisione', icon: ClipboardList },
];

const AUTOSAVE_KEY = 'dietamenu-piano-editor-draft-v1';
const TEMPLATES_KEY = 'dietamenu-piano-templates-v1';
const AUTOSAVE_INTERVAL_MS = 10_000;

interface PlanDraft {
  patient_id: string;
  title: string;
  start_date: string;
  weeks_total: number;
  unique_weeks: number;
  meals: PlanEditorMeal[];
}

interface PlanTemplate {
  id: string;
  name: string;
  description: string;
  weeks_total: number;
  unique_weeks: number;
  meals: PlanEditorMeal[];
  created_at: string;
}

function newClientId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyDraft(): PlanDraft {
  const today = new Date().toISOString().split('T')[0];
  return {
    patient_id: '',
    title: '',
    start_date: today,
    weeks_total: 4,
    unique_weeks: 1,
    meals: [],
  };
}

function computeEndDate(startDate: string, weeks: number): string {
  if (!startDate) return '';
  const d = new Date(startDate + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + weeks * 7 - 1);
  return d.toISOString().split('T')[0];
}

function loadAutosave(): PlanDraft | null {
  try {
    const raw = window.localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { draft?: PlanDraft; savedAt?: string };
    if (parsed && parsed.draft && typeof parsed.draft === 'object') {
      return parsed.draft as PlanDraft;
    }
  } catch {
    // ignore
  }
  return null;
}

function loadTemplates(): PlanTemplate[] {
  try {
    const raw = window.localStorage.getItem(TEMPLATES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as PlanTemplate[]) : [];
  } catch {
    return [];
  }
}

function saveTemplates(list: PlanTemplate[]): void {
  try {
    window.localStorage.setItem(TEMPLATES_KEY, JSON.stringify(list));
  } catch {
    // ignore (quota / private mode)
  }
}

/** Sprint 9.B closure — Converte DietPlanDetail (GET /api/pro/plans/:id) in PlanDraft per hydrate edit mode. */
function planDetailToDraft(detail: DietPlanDetail): PlanDraft {
  const meals: PlanEditorMeal[] = detail.meals.map((m) => ({
    client_id: newClientId(),
    day_of_week: m.day_of_week,
    meal_type: m.meal_type,
    name: m.name,
    items: m.items.map((it) => ({
      ingredient: it.ingredient,
      quantity: it.quantity,
      unit: it.unit,
      calories: it.calories ?? null,
    })),
    week_of_plan: m.week_of_plan,
    variant_group: m.variant_group,
    variant_label: m.variant_label,
    is_fasting: m.is_fasting,
  }));
  // weeks_total = diff giorni / 7 (rounded), min 1, cap 12
  const startMs = new Date(detail.plan.start_date + 'T12:00:00').getTime();
  const endMs = new Date(detail.plan.end_date + 'T12:00:00').getTime();
  const days = Number.isFinite(startMs) && Number.isFinite(endMs)
    ? Math.max(1, Math.round((endMs - startMs) / 86400000) + 1)
    : 7;
  const weeks_total = Math.min(12, Math.max(1, Math.round(days / 7)));
  // unique_weeks = max(week_of_plan) tra meals, default 1
  const uniqueWeeks = meals.reduce((acc, m) => Math.max(acc, m.week_of_plan ?? 1), 1);
  return {
    patient_id: detail.plan.patient_id,
    title: detail.plan.title,
    start_date: detail.plan.start_date,
    weeks_total,
    unique_weeks: Math.min(uniqueWeeks, weeks_total),
    meals,
  };
}

/**
 * Sprint 9.B — Wizard editor piano alimentare (porting da SPA vanilla).
 * 4 step: cliente → durata → meal builder → revisione + salva.
 * Features: autosave localStorage, validation dietetica, drag&drop ingredienti,
 * copy/paste pasto, AI suggest contestuale, varianti A/B/C/D, fasting,
 * multi-settimana, copia settimana N→M, template salvabili, clone multi-paziente.
 */
export default function PianoEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [search] = useSearchParams();
  const isEdit = !!id && id !== 'new';
  const preselectPatientId = search.get('patient_id');

  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<PlanDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [activeWeek, setActiveWeek] = useState<number>(1);
  const [clipboard, setClipboard] = useState<PlanEditorMeal | null>(null);
  const [showResume, setShowResume] = useState<boolean>(false);
  const [autosaveAt, setAutosaveAt] = useState<Date | null>(null);
  const [templates, setTemplates] = useState<PlanTemplate[]>(() => loadTemplates());
  const [showCloneModal, setShowCloneModal] = useState<{ planId: string; sourceName: string } | null>(null);
  const [cloneTargets, setCloneTargets] = useState<Set<string>>(new Set());
  const [savedPlanId, setSavedPlanId] = useState<string | null>(null);

  const { data: patients, isLoading: patientsLoading } = usePatients();
  const createMutation = useCreateDietPlan();
  const cloneMutation = useCloneDietPlan();
  const updateMutation = useUpdateDietPlan();
  const createTemplateMutation = useCreatePlanTemplate();

  // Sprint 9.B closure — edit mode: fetch piano remoto e hydrate draft
  const { data: editPlanData, isLoading: editPlanLoading, error: editPlanError } = useDietPlan(isEdit ? (id || null) : null);
  const [hydrated, setHydrated] = useState<boolean>(false);

  // Hydration draft da GET /api/pro/plans/:id (1 sola volta in edit mode)
  useEffect(() => {
    if (!isEdit) return;
    if (hydrated) return;
    if (!editPlanData) return;
    setDraft(planDetailToDraft(editPlanData));
    setHydrated(true);
  }, [isEdit, editPlanData, hydrated]);

  // Riprendi autosave al mount
  useEffect(() => {
    if (isEdit) return; // edit mode non riprende: caricherebbe stale
    const saved = loadAutosave();
    if (saved && (saved.meals.length > 0 || saved.title || saved.patient_id)) {
      setShowResume(true);
    }
  }, [isEdit]);

  // Pre-fill patient_id da query string (e.g., redirect da dettaglio paziente)
  useEffect(() => {
    if (!isEdit && preselectPatientId && !draft.patient_id) {
      setDraft((d) => ({ ...d, patient_id: preselectPatientId }));
    }
  }, [isEdit, preselectPatientId, draft.patient_id]);

  // Autosave ogni 10s
  useEffect(() => {
    if (isEdit) return; // edit mode non autosalva (eviterebbe override stale)
    const t = window.setInterval(() => {
      try {
        const payload = { draft, savedAt: new Date().toISOString() };
        window.localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(payload));
        setAutosaveAt(new Date());
      } catch {
        // ignore
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [draft, isEdit]);

  const resumeAutosave = () => {
    const saved = loadAutosave();
    if (saved) {
      setDraft(saved);
      setShowResume(false);
    }
  };

  const dismissAutosave = () => {
    try {
      window.localStorage.removeItem(AUTOSAVE_KEY);
    } catch {
      // ignore
    }
    setShowResume(false);
  };

  // ─── Derived ───────────────────────────────────────────────────────────
  const endDate = useMemo(
    () => computeEndDate(draft.start_date, draft.weeks_total),
    [draft.start_date, draft.weeks_total]
  );

  const selectedPatient = useMemo(
    () => (patients || []).find((p) => p.id === draft.patient_id) || null,
    [patients, draft.patient_id]
  );

  // ─── Validation ────────────────────────────────────────────────────────
  const step1Valid = !!draft.patient_id && !!draft.title.trim();
  const step2Valid = !!draft.start_date && draft.weeks_total >= 1 && draft.weeks_total <= 12;
  const step3Valid = draft.meals.length > 0 && draft.meals.every((m) =>
    m.is_fasting === 1 || (m.name.trim().length > 0 && m.items.every((it) => it.ingredient.trim().length > 0))
  );

  const canSave = step1Valid && step2Valid && step3Valid;

  // ─── Validazione dietetica: stats kcal/macros ─────────────────────────
  const dietStats = useMemo(() => {
    const byWeekDay = new Map<string, number>();
    for (let w = 1; w <= draft.unique_weeks; w++) {
      for (let d = 1; d <= 7; d++) {
        const slotMeals = draft.meals.filter(
          (m) => (m.week_of_plan ?? 1) === w && m.day_of_week === d
        );
        // Per ogni meal_type prendi variante A (o single) e somma kcal
        const seenSlots = new Set<MealType>();
        let dayKcal = 0;
        for (const m of slotMeals) {
          if (seenSlots.has(m.meal_type)) {
            // se ha variant_label, conta solo la A
            if (m.variant_label && m.variant_label !== 'A') continue;
          }
          if (m.is_fasting === 1) {
            seenSlots.add(m.meal_type);
            continue;
          }
          for (const it of m.items) dayKcal += it.calories ?? 0;
          seenSlots.add(m.meal_type);
        }
        byWeekDay.set(`${w}-${d}`, dayKcal);
      }
    }
    let warnings: string[] = [];
    for (const [k, v] of byWeekDay) {
      if (v > 3500) warnings.push(`Settimana/Giorno ${k}: ${v} kcal · fuori range (max 3500)`);
      if (v > 0 && v < 1200) warnings.push(`Settimana/Giorno ${k}: ${v} kcal · troppo restrittivo (min 1200)`);
    }
    return { byWeekDay, warnings };
  }, [draft.meals, draft.unique_weeks]);

  // ─── Step nav ──────────────────────────────────────────────────────────
  const goNext = () => {
    setError(null);
    if (step === 1 && !step1Valid) {
      setError('Seleziona un paziente e inserisci un titolo');
      return;
    }
    if (step === 2 && !step2Valid) {
      setError('Inserisci una data di inizio valida e settimane 1-12');
      return;
    }
    if (step === 3 && !step3Valid) {
      setError('Aggiungi almeno un pasto. Tutti i pasti devono avere nome e ingredienti non vuoti.');
      return;
    }
    if (step < 4) setStep((step + 1) as Step);
  };

  const goPrev = () => {
    setError(null);
    if (step > 1) setStep((step - 1) as Step);
  };

  // ─── Meal management ──────────────────────────────────────────────────
  const updateMeal = (clientId: string, next: PlanEditorMeal) => {
    setDraft((d) => ({
      ...d,
      meals: d.meals.map((m) => (m.client_id === clientId ? next : m)),
    }));
  };

  const removeMeal = (clientId: string) => {
    setDraft((d) => ({
      ...d,
      meals: d.meals.filter((m) => m.client_id !== clientId),
    }));
  };

  const addMeal = (dayOfWeek: number, mealType: MealType, weekOfPlan: number) => {
    const newMeal: PlanEditorMeal = {
      client_id: newClientId(),
      day_of_week: dayOfWeek,
      meal_type: mealType,
      name: '',
      items: [],
      week_of_plan: draft.unique_weeks > 1 ? weekOfPlan : null,
      variant_group: null,
      variant_label: null,
      is_fasting: 0,
    };
    setDraft((d) => ({ ...d, meals: [...d.meals, newMeal] }));
  };

  const addVariant = (
    slotMeals: PlanEditorMeal[],
    dayOfWeek: number,
    mealType: MealType,
    weekOfPlan: number
  ) => {
    // Trova variant_group esistente o crea uno nuovo
    let variantGroup: number | null = null;
    const withGroup = slotMeals.find((m) => m.variant_group !== null);
    if (withGroup) {
      variantGroup = withGroup.variant_group!;
    } else {
      // Calcola nuovo variant_group univoco
      const used = new Set(draft.meals.map((m) => m.variant_group).filter((v): v is number => v !== null));
      let candidate = 1;
      while (used.has(candidate)) candidate++;
      variantGroup = candidate;
    }
    // Determina prossimo label
    const existingLabels = new Set(slotMeals.map((m) => m.variant_label).filter((l): l is string => !!l));
    const nextLabel = ['A', 'B', 'C', 'D'].find((l) => !existingLabels.has(l));
    if (!nextLabel) return;

    // Se è la prima variante (cioè il pasto esistente è singolo e non ha variant_group),
    // aggiorna anche quello esistente a variant_label='A'
    const updates: PlanEditorMeal[] = [];
    if (slotMeals.length === 1 && slotMeals[0].variant_group === null) {
      updates.push({
        ...slotMeals[0],
        variant_group: variantGroup,
        variant_label: 'A',
      });
    }

    const newMeal: PlanEditorMeal = {
      client_id: newClientId(),
      day_of_week: dayOfWeek,
      meal_type: mealType,
      name: '',
      items: [],
      week_of_plan: draft.unique_weeks > 1 ? weekOfPlan : null,
      variant_group: variantGroup,
      variant_label: nextLabel,
      is_fasting: 0,
    };

    setDraft((d) => ({
      ...d,
      meals: [
        ...d.meals.map((m) => {
          const u = updates.find((x) => x.client_id === m.client_id);
          return u || m;
        }),
        newMeal,
      ],
    }));
  };

  const copyMeal = useCallback((meal: PlanEditorMeal) => {
    setClipboard({ ...meal, items: meal.items.map((it) => ({ ...it })) });
  }, []);

  const pasteMeal = (dayOfWeek: number, mealType: MealType, weekOfPlan: number) => {
    if (!clipboard) return;
    const pasted: PlanEditorMeal = {
      ...clipboard,
      client_id: newClientId(),
      day_of_week: dayOfWeek,
      meal_type: mealType,
      week_of_plan: draft.unique_weeks > 1 ? weekOfPlan : null,
      variant_group: null,
      variant_label: null,
    };
    setDraft((d) => ({ ...d, meals: [...d.meals, pasted] }));
  };

  const copyWeek = (sourceWeek: number, targetWeek: number) => {
    if (sourceWeek === targetWeek) return;
    // Rimuovi pasti target esistenti, replicali da source
    setDraft((d) => {
      const otherWeeks = d.meals.filter((m) => (m.week_of_plan ?? 1) !== targetWeek);
      const sourceMeals = d.meals.filter((m) => (m.week_of_plan ?? 1) === sourceWeek);
      const cloned = sourceMeals.map((m) => ({
        ...m,
        client_id: newClientId(),
        week_of_plan: targetWeek,
        items: m.items.map((it) => ({ ...it })),
      }));
      return { ...d, meals: [...otherWeeks, ...cloned] };
    });
  };

  // ─── Templates (localStorage) ─────────────────────────────────────────
  const saveAsTemplate = () => {
    const name = window.prompt('Nome template:', draft.title || 'Template piano');
    if (!name) return;
    const tpl: PlanTemplate = {
      id: `tpl-${Date.now()}`,
      name: name.trim(),
      description: `${draft.weeks_total} sett · ${draft.meals.length} pasti`,
      weeks_total: draft.weeks_total,
      unique_weeks: draft.unique_weeks,
      meals: draft.meals.map((m) => ({ ...m, items: m.items.map((it) => ({ ...it })) })),
      created_at: new Date().toISOString(),
    };
    const next = [...templates, tpl];
    setTemplates(next);
    saveTemplates(next);
  };

  const applyTemplate = (tplId: string) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    if (draft.meals.length > 0) {
      const ok = window.confirm('Il piano corrente verrà sovrascritto. Continuare?');
      if (!ok) return;
    }
    setDraft((d) => ({
      ...d,
      weeks_total: tpl.weeks_total,
      unique_weeks: tpl.unique_weeks,
      meals: tpl.meals.map((m) => ({ ...m, client_id: newClientId(), items: m.items.map((it) => ({ ...it })) })),
    }));
    setActiveWeek(1);
  };

  const deleteTemplate = (tplId: string) => {
    const next = templates.filter((t) => t.id !== tplId);
    setTemplates(next);
    saveTemplates(next);
  };

  // ─── Save ─────────────────────────────────────────────────────────────
  const buildPayload = () => ({
    patient_id: draft.patient_id,
    title: draft.title.trim(),
    start_date: draft.start_date,
    end_date: endDate,
    meals: draft.meals.map((m) => ({
      day_of_week: m.day_of_week,
      meal_type: m.meal_type,
      name: m.name.trim() || (m.is_fasting === 1 ? 'Digiuno' : 'Pasto'),
      items: m.is_fasting === 1
        ? []
        : m.items
            .filter((it) => it.ingredient.trim())
            .map((it) => ({
              ingredient: it.ingredient.trim(),
              quantity: it.quantity ? String(it.quantity).trim() || null : null,
              unit: it.unit ? String(it.unit).trim() || null : null,
            })),
      week_of_plan: m.week_of_plan ?? null,
      variant_group: m.variant_group ?? null,
      variant_label: m.variant_label ?? null,
      is_fasting: m.is_fasting ?? 0,
    })),
  });

  const handleSave = () => {
    setError(null);
    if (!canSave) {
      setError('Compila tutti i campi obbligatori prima di salvare');
      return;
    }
    const payload = buildPayload();
    void (async () => {
      try {
        if (isEdit && id) {
          // Edit mode: PUT /api/pro/plans/:id — sostituisce meals atomic
          await updateMutation.mutateAsync({
            planId: id,
            body: {
              title: payload.title,
              start_date: payload.start_date,
              end_date: payload.end_date,
              meals: payload.meals,
            },
          });
          navigate('/piani');
          return;
        }
        const res = await createMutation.mutateAsync(payload);
        // Cleanup autosave
        try {
          window.localStorage.removeItem(AUTOSAVE_KEY);
        } catch {
          // ignore
        }
        setSavedPlanId(res.plan_id);
        // Apri modale clone multi-paziente se ci sono altri pazienti disponibili
        const otherPatients = (patients || []).filter((p) => p.id !== draft.patient_id);
        if (otherPatients.length > 0) {
          setShowCloneModal({ planId: res.plan_id, sourceName: draft.title.trim() });
        } else {
          navigate('/piani');
        }
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setError(e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err));
      }
    })();
  };

  const handleCloneToMany = () => {
    if (!showCloneModal) return;
    const targets = Array.from(cloneTargets);
    if (targets.length === 0) {
      setShowCloneModal(null);
      navigate('/piani');
      return;
    }
    void (async () => {
      try {
        for (const pid of targets) {
          await cloneMutation.mutateAsync({
            sourceId: showCloneModal.planId,
            body: { patient_id: pid, title: showCloneModal.sourceName, start_date: draft.start_date },
          });
        }
        setShowCloneModal(null);
        setCloneTargets(new Set());
        navigate('/piani');
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setError(e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err));
      }
    })();
  };

  const saving = createMutation.isPending || cloneMutation.isPending || updateMutation.isPending;

  // Sprint 9.B closure — Salva come template DB (server-side, condivisibile cross-device)
  const handleSaveAsTemplateRemote = () => {
    setError(null);
    if (draft.meals.length === 0) {
      setError('Aggiungi almeno un pasto prima di salvare come template');
      return;
    }
    const name = window.prompt(
      'Nome template (rimarrà visibile in /piani/templates):',
      draft.title.trim() || 'Template piano'
    );
    if (!name || !name.trim()) return;
    const description = window.prompt(
      'Descrizione breve (opzionale, lascia vuoto per saltare):',
      ''
    );
    const templateJson = JSON.stringify({
      weeks: draft.unique_weeks,
      meals: draft.meals.map((m) => ({
        day_of_week: m.day_of_week,
        meal_type: m.meal_type,
        name: m.name.trim() || (m.is_fasting === 1 ? 'Digiuno' : 'Pasto'),
        items: m.is_fasting === 1
          ? []
          : m.items
              .filter((it) => it.ingredient.trim())
              .map((it) => ({
                ingredient: it.ingredient.trim(),
                quantity: it.quantity ? String(it.quantity).trim() || null : null,
                unit: it.unit ? String(it.unit).trim() || null : null,
              })),
        week_of_plan: m.week_of_plan ?? null,
        variant_group: m.variant_group ?? null,
        variant_label: m.variant_label ?? null,
        is_fasting: m.is_fasting ?? 0,
      })),
    });
    void (async () => {
      try {
        await createTemplateMutation.mutateAsync({
          name: name.trim(),
          description: description?.trim() || null,
          weeks: draft.unique_weeks,
          template_json: templateJson,
        });
        window.alert('Template salvato. Lo trovi in /piani/templates');
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setError(e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err));
      }
    })();
  };

  // ─── KCal target stimato (heuristic) ─────────────────────────────────
  const kcalTarget: number | undefined = useMemo(() => {
    // Heuristic per warning solo se contesto presente; non visibile altrove.
    // Default: 2000 kcal (placeholder). In futuro: leggere da pazient context.
    return 2000;
  }, []);

  return (
    <>
      <ProTopBar />

      <div style={{ padding: '4px 0 18px' }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1.6,
            color: 'var(--mute)',
            textTransform: 'uppercase',
          }}
        >
          Studio Pro · Piani · {isEdit ? 'Modifica' : 'Nuovo piano'}
        </div>
        <h1
          style={{
            fontFamily: 'var(--display)',
            fontWeight: 800,
            fontSize: 32,
            letterSpacing: -1,
            margin: '6px 0 0',
            lineHeight: 1.05,
          }}
        >
          {draft.title || 'Nuovo piano'}
        </h1>
        {autosaveAt && !isEdit && (
          <div style={{ fontSize: 11, color: 'var(--mute)', marginTop: 6, fontFamily: 'var(--mono)' }}>
            Bozza salvata localmente alle {autosaveAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        )}
      </div>

      {/* Banner riprendi autosave */}
      {showResume && (
        <div
          role="region"
          aria-label="Ripristina bozza"
          style={{
            background: 'var(--orange-soft, #fff3d6)',
            border: '1px solid var(--orange)',
            borderRadius: 10,
            padding: 14,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <AlertTriangle size={18} color="var(--orange-deep, #a87c00)" />
          <div style={{ flex: 1, fontSize: 13, color: 'var(--ink)' }}>
            È disponibile una bozza salvata localmente. Riprendere?
          </div>
          <button
            type="button"
            onClick={resumeAutosave}
            style={{
              padding: '7px 14px',
              background: 'var(--ink)',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Riprendi
          </button>
          <button
            type="button"
            onClick={dismissAutosave}
            style={{
              padding: '7px 14px',
              background: 'transparent',
              color: 'var(--ink-2)',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Scarta
          </button>
        </div>
      )}

      {/* Step indicator */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 18,
          padding: 4,
          background: 'var(--surface-2)',
          borderRadius: 10,
          flexWrap: 'wrap',
        }}
      >
        {STEPS.map((s) => {
          const active = step === s.n;
          const done = step > s.n;
          const Icon = s.icon;
          return (
            <div
              key={s.n}
              style={{
                flex: '1 1 120px',
                padding: '10px 12px',
                background: active ? 'var(--surface)' : 'transparent',
                borderRadius: 8,
                boxShadow: active ? 'var(--shadow-sm)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 110,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: done ? 'var(--green-deep)' : active ? 'var(--ink)' : 'var(--line)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {done ? <Check size={12} /> : <Icon size={12} />}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: active ? 'var(--ink)' : done ? 'var(--green-deep)' : 'var(--mute)',
                }}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit mode banners — Sprint 9.B closure */}
      {isEdit && editPlanLoading && !hydrated && (
        <div
          role="status"
          style={{
            background: 'var(--paper)', border: '1px solid var(--line)',
            borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--mute)',
            marginBottom: 14,
          }}
        >
          Caricamento piano in corso…
        </div>
      )}
      {isEdit && editPlanError && (
        <div
          role="alert"
          style={{
            background: 'var(--red-soft, #fff0f0)', border: '1px solid var(--red)',
            borderRadius: 8, padding: 12, fontSize: 13, color: 'var(--red)',
            marginBottom: 14,
          }}
        >
          Impossibile caricare il piano: {editPlanError instanceof ApiError ? editPlanError.message : String(editPlanError)}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            background: 'var(--red-soft, #fff0f0)',
            border: '1px solid var(--red)',
            borderRadius: 8,
            padding: 12,
            fontSize: 13,
            color: 'var(--red)',
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Step content */}
      {step === 1 && (
        <Step1Cliente
          draft={draft}
          setDraft={setDraft}
          patients={patients || []}
          patientsLoading={patientsLoading}
          templates={templates}
          onApplyTemplate={applyTemplate}
          onDeleteTemplate={deleteTemplate}
        />
      )}

      {step === 2 && <Step2Durata draft={draft} setDraft={setDraft} endDate={endDate} />}

      {step === 3 && (
        <Step3MealBuilder
          draft={draft}
          activeWeek={activeWeek}
          setActiveWeek={setActiveWeek}
          clipboard={clipboard}
          onUpdateMeal={updateMeal}
          onRemoveMeal={removeMeal}
          onAddMeal={addMeal}
          onAddVariant={addVariant}
          onCopyMeal={copyMeal}
          onPasteMeal={pasteMeal}
          onCopyWeek={copyWeek}
          kcalTarget={kcalTarget}
          dietWarnings={dietStats.warnings}
        />
      )}

      {step === 4 && (
        <Step4Review
          draft={draft}
          endDate={endDate}
          patientName={selectedPatient?.name || draft.patient_id}
          dietWarnings={dietStats.warnings}
          templates={templates}
          onSaveAsTemplate={saveAsTemplate}
        />
      )}

      {/* Footer nav */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: 22,
          paddingTop: 16,
          borderTop: '1px solid var(--line-3)',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/piani')}
          style={{
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            padding: '9px 14px',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <X size={14} /> Annulla
        </button>
        <div style={{ flex: 1 }} />
        {step > 1 && (
          <button
            type="button"
            onClick={goPrev}
            style={{
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              color: 'var(--ink)',
              padding: '9px 14px',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ChevronLeft size={14} /> Indietro
          </button>
        )}
        {step < 4 && (
          <button
            type="button"
            onClick={goNext}
            style={{
              border: 0,
              background: 'var(--ink)',
              color: '#fff',
              padding: '9px 18px',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Avanti <ChevronRight size={14} />
          </button>
        )}
        {step === 4 && (
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button
              type="button"
              onClick={handleSaveAsTemplateRemote}
              disabled={createTemplateMutation.isPending || draft.meals.length === 0}
              title="Salva la struttura come template riutilizzabile"
              style={{
                border: '1px solid var(--line)',
                background: 'var(--paper)',
                color: 'var(--ink)',
                padding: '9px 14px',
                borderRadius: 8,
                fontWeight: 700,
                cursor: draft.meals.length === 0 ? 'not-allowed' : 'pointer',
                opacity: draft.meals.length === 0 ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
            >
              {createTemplateMutation.isPending ? 'Salvataggio…' : 'Salva come template'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || saving}
              style={{
                border: 0,
                background: canSave && !saving ? 'var(--green-deep)' : 'var(--line)',
                color: '#fff',
                padding: '9px 18px',
                borderRadius: 8,
                fontWeight: 700,
                cursor: canSave && !saving ? 'pointer' : 'not-allowed',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Save size={14} />
              {saving ? 'Salvataggio…' : isEdit ? 'Salva modifiche' : 'Crea piano'}
            </button>
          </div>
        )}
      </div>

      {/* Modale clone multi-paziente */}
      {showCloneModal && (
        <CloneToManyModal
          patients={(patients || []).filter((p) => p.id !== draft.patient_id)}
          selected={cloneTargets}
          onToggle={(pid) => {
            setCloneTargets((prev) => {
              const next = new Set(prev);
              if (next.has(pid)) next.delete(pid);
              else next.add(pid);
              return next;
            });
          }}
          onCancel={() => {
            setShowCloneModal(null);
            navigate('/piani');
          }}
          onConfirm={handleCloneToMany}
          loading={saving}
          sourceName={showCloneModal.sourceName}
          savedPlanId={savedPlanId}
        />
      )}
    </>
  );
}

// ============================================================================
// STEP 1 — Cliente
// ============================================================================
function Step1Cliente({
  draft,
  setDraft,
  patients,
  patientsLoading,
  templates,
  onApplyTemplate,
  onDeleteTemplate,
}: {
  draft: PlanDraft;
  setDraft: React.Dispatch<React.SetStateAction<PlanDraft>>;
  patients: Array<{ id: string; name: string; human_id: string | null; email: string | null }>;
  patientsLoading: boolean;
  templates: PlanTemplate[];
  onApplyTemplate: (id: string) => void;
  onDeleteTemplate: (id: string) => void;
}) {
  const [searchQ, setSearchQ] = useState('');
  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.human_id && p.human_id.toLowerCase().includes(q)) ||
        (p.email && p.email.toLowerCase().includes(q))
    );
  }, [patients, searchQ]);

  return (
    <section className={styles.card} style={{ padding: 20 }}>
      <h2 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 18, margin: '0 0 14px' }}>
        Cliente e titolo piano
      </h2>

      <FieldBlock label="Titolo piano">
        <input
          type="text"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          placeholder="es. Piano Maggio 2026 · Mediterranea 1800kcal"
          style={inputStyle}
          maxLength={120}
        />
      </FieldBlock>

      <div style={{ marginTop: 14 }}>
        <FieldBlock label="Cerca paziente">
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Nome, PZ-ID, email…"
            style={inputStyle}
          />
        </FieldBlock>
      </div>

      <div
        style={{
          marginTop: 12,
          maxHeight: 240,
          overflowY: 'auto',
          border: '1px solid var(--line)',
          borderRadius: 8,
          background: 'var(--surface)',
        }}
      >
        {patientsLoading ? (
          <div style={{ padding: 14, fontSize: 13, color: 'var(--mute)' }}>Caricamento pazienti…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: 'var(--mute)' }}>
            Nessun paziente trovato. {patients.length === 0 ? 'Aggiungi pazienti dal portale Clienti.' : ''}
          </div>
        ) : (
          filtered.map((p) => {
            const active = draft.patient_id === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setDraft((d) => ({ ...d, patient_id: p.id }))}
                style={{
                  display: 'flex',
                  width: '100%',
                  padding: '10px 12px',
                  border: 0,
                  borderBottom: '1px solid var(--line-3)',
                  background: active ? 'var(--green-soft)' : 'var(--surface)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--mute)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                    {p.human_id || '—'}{p.email ? ` · ${p.email}` : ''}
                  </div>
                </div>
                {active && <Check size={14} color="var(--green-deep)" />}
              </button>
            );
          })
        )}
      </div>

      {templates.length > 0 && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 14,
            borderTop: '1px dashed var(--line)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--mute)',
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            Template salvati ({templates.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {templates.map((t) => (
              <div
                key={t.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 10,
                  background: 'var(--surface-2)',
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--mute)' }}>{t.description}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onApplyTemplate(t.id)}
                  style={{
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    padding: '5px 10px',
                    borderRadius: 6,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Applica
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Elimina template "${t.name}"?`)) onDeleteTemplate(t.id);
                  }}
                  aria-label="Elimina template"
                  style={{
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    color: 'var(--red)',
                    padding: '5px 8px',
                    borderRadius: 6,
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Elimina
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================================
// STEP 2 — Durata
// ============================================================================
function Step2Durata({
  draft,
  setDraft,
  endDate,
}: {
  draft: PlanDraft;
  setDraft: React.Dispatch<React.SetStateAction<PlanDraft>>;
  endDate: string;
}) {
  return (
    <section className={styles.card} style={{ padding: 20 }}>
      <h2 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 18, margin: '0 0 14px' }}>
        Durata del piano
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <FieldBlock label="Data inizio">
          <input
            type="date"
            value={draft.start_date}
            onChange={(e) => setDraft((d) => ({ ...d, start_date: e.target.value }))}
            style={inputStyle}
          />
        </FieldBlock>
        <FieldBlock label="Settimane totali (1-12)">
          <input
            type="number"
            min={1}
            max={12}
            value={draft.weeks_total}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setDraft((d) => ({ ...d, weeks_total: Number.isNaN(v) ? 1 : Math.max(1, Math.min(12, v)) }));
            }}
            style={inputStyle}
          />
        </FieldBlock>
        <FieldBlock label="Data fine (calcolata)">
          <input type="text" value={endDate} disabled style={{ ...inputStyle, opacity: 0.7 }} />
        </FieldBlock>
        <FieldBlock label="Settimane uniche (1-4)">
          <input
            type="number"
            min={1}
            max={Math.min(4, draft.weeks_total)}
            value={draft.unique_weeks}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              const max = Math.min(4, draft.weeks_total);
              setDraft((d) => ({ ...d, unique_weeks: Number.isNaN(v) ? 1 : Math.max(1, Math.min(max, v)) }));
            }}
            style={inputStyle}
          />
        </FieldBlock>
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--mute)', lineHeight: 1.55 }}>
        <b>Settimane uniche</b>: 1 = stesso layout ripetuto. 2-4 = layout diverso per ogni settimana del ciclo
        (es. 4 settimane uniche su 12 totali = 3 cicli).
      </div>
    </section>
  );
}

// ============================================================================
// STEP 3 — Meal builder
// ============================================================================
function Step3MealBuilder({
  draft,
  activeWeek,
  setActiveWeek,
  clipboard,
  onUpdateMeal,
  onRemoveMeal,
  onAddMeal,
  onAddVariant,
  onCopyMeal,
  onPasteMeal,
  onCopyWeek,
  kcalTarget,
  dietWarnings,
}: {
  draft: PlanDraft;
  activeWeek: number;
  setActiveWeek: (n: number) => void;
  clipboard: PlanEditorMeal | null;
  onUpdateMeal: (clientId: string, next: PlanEditorMeal) => void;
  onRemoveMeal: (clientId: string) => void;
  onAddMeal: (dayOfWeek: number, mealType: MealType, weekOfPlan: number) => void;
  onAddVariant: (slotMeals: PlanEditorMeal[], dayOfWeek: number, mealType: MealType, weekOfPlan: number) => void;
  onCopyMeal: (meal: PlanEditorMeal) => void;
  onPasteMeal: (dayOfWeek: number, mealType: MealType, weekOfPlan: number) => void;
  onCopyWeek: (from: number, to: number) => void;
  kcalTarget?: number;
  dietWarnings: string[];
}) {
  const showWeekTabs = draft.unique_weeks > 1;
  const safeActiveWeek = Math.min(activeWeek, draft.unique_weeks);
  const [copyFrom, setCopyFrom] = useState<number>(1);

  // Stable: usa effect per riallineare activeWeek se unique_weeks scende
  const prevUWRef = useRef(draft.unique_weeks);
  useEffect(() => {
    if (draft.unique_weeks !== prevUWRef.current) {
      if (activeWeek > draft.unique_weeks) setActiveWeek(1);
      prevUWRef.current = draft.unique_weeks;
    }
  }, [draft.unique_weeks, activeWeek, setActiveWeek]);

  return (
    <section>
      {showWeekTabs && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            marginBottom: 14,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {Array.from({ length: draft.unique_weeks }, (_, i) => i + 1).map((w) => {
            const isActive = w === safeActiveWeek;
            const count = draft.meals.filter((m) => (m.week_of_plan ?? 1) === w).length;
            return (
              <button
                key={w}
                type="button"
                onClick={() => setActiveWeek(w)}
                style={{
                  padding: '7px 14px',
                  border: '1px solid var(--line)',
                  background: isActive ? 'var(--ink)' : 'var(--surface)',
                  color: isActive ? '#fff' : 'var(--ink-2)',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'var(--mono)',
                  letterSpacing: 0.5,
                }}
              >
                Set {w}
                {count > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      padding: '1px 5px',
                      background: isActive ? 'rgba(255,255,255,0.2)' : 'var(--surface-2)',
                      borderRadius: 3,
                      fontSize: 10,
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <select
            value={copyFrom}
            onChange={(e) => setCopyFrom(parseInt(e.target.value, 10))}
            style={{ ...inputStyle, width: 'auto', padding: '6px 8px' }}
            aria-label="Copia da settimana"
          >
            {Array.from({ length: draft.unique_weeks }, (_, i) => i + 1).map((w) => (
              <option key={w} value={w}>
                Da set. {w}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (copyFrom === safeActiveWeek) return;
              const ok = window.confirm(
                `Copiare i pasti dalla settimana ${copyFrom} alla ${safeActiveWeek}? I pasti correnti della settimana ${safeActiveWeek} saranno sostituiti.`
              );
              if (ok) onCopyWeek(copyFrom, safeActiveWeek);
            }}
            disabled={copyFrom === safeActiveWeek}
            style={{
              padding: '6px 10px',
              background: copyFrom === safeActiveWeek ? 'var(--line)' : 'var(--surface)',
              color: copyFrom === safeActiveWeek ? 'var(--mute)' : 'var(--ink)',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontSize: 11.5,
              fontWeight: 600,
              cursor: copyFrom === safeActiveWeek ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <CopyIcon size={11} /> → set. {safeActiveWeek}
          </button>
        </div>
      )}

      {dietWarnings.length > 0 && (
        <div
          role="alert"
          style={{
            background: 'var(--orange-soft, #fff3d6)',
            border: '1px solid var(--orange)',
            borderRadius: 8,
            padding: 12,
            fontSize: 12.5,
            color: 'var(--orange-deep, #a87c00)',
            marginBottom: 14,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> Avvertimenti dietetici ({dietWarnings.length})
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
            {dietWarnings.slice(0, 5).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {dietWarnings.length > 5 && <li>… e altri {dietWarnings.length - 5}</li>}
          </ul>
        </div>
      )}

      <PianoWeekTab
        weekNumber={safeActiveWeek}
        meals={draft.meals}
        patientId={draft.patient_id}
        clipboard={clipboard}
        onUpdateMeal={onUpdateMeal}
        onRemoveMeal={onRemoveMeal}
        onAddMeal={onAddMeal}
        onAddVariant={onAddVariant}
        onCopyMeal={onCopyMeal}
        onPasteMeal={onPasteMeal}
        kcalDailyTarget={kcalTarget}
      />
    </section>
  );
}

// ============================================================================
// STEP 4 — Review
// ============================================================================
function Step4Review({
  draft,
  endDate,
  patientName,
  dietWarnings,
  onSaveAsTemplate,
}: {
  draft: PlanDraft;
  endDate: string;
  patientName: string;
  dietWarnings: string[];
  templates: PlanTemplate[];
  onSaveAsTemplate: () => void;
}) {
  const variantGroups = new Set(
    draft.meals.map((m) => m.variant_group).filter((v): v is number => v !== null)
  ).size;
  const fastingSlots = draft.meals.filter((m) => m.is_fasting === 1).length;
  const totalKcalApprox = draft.meals.reduce(
    (sum, m) => (m.is_fasting === 1 ? sum : sum + m.items.reduce((a, it) => a + (it.calories ?? 0), 0)),
    0
  );

  return (
    <section className={styles.card} style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 14,
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 18, margin: 0, flex: 1 }}>
          Revisione finale
        </h2>
        <button
          type="button"
          onClick={onSaveAsTemplate}
          disabled={draft.meals.length === 0}
          style={{
            padding: '7px 12px',
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            border: '1px solid var(--line)',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 12,
            cursor: draft.meals.length === 0 ? 'not-allowed' : 'pointer',
            opacity: draft.meals.length === 0 ? 0.5 : 1,
          }}
        >
          Salva come template
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
          marginBottom: 18,
        }}
      >
        <ReviewKpi label="Paziente" value={patientName} />
        <ReviewKpi label="Settimane" value={`${draft.weeks_total} (${draft.unique_weeks} uniche)`} />
        <ReviewKpi label="Pasti totali" value={String(draft.meals.length)} />
        <ReviewKpi label="Varianti" value={String(variantGroups)} />
        <ReviewKpi label="Slot digiuno" value={String(fastingSlots)} />
        <ReviewKpi label="Kcal stimato" value={totalKcalApprox > 0 ? `~${totalKcalApprox}` : '—'} />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Dati piano
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.65 }}>
          <b>{draft.title}</b>
          <br />
          Dal {draft.start_date} al {endDate}
        </div>
      </div>

      {dietWarnings.length > 0 && (
        <div
          style={{
            background: 'var(--orange-soft, #fff3d6)',
            border: '1px solid var(--orange)',
            borderRadius: 8,
            padding: 12,
            fontSize: 12.5,
            color: 'var(--orange-deep, #a87c00)',
            marginBottom: 14,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> Avvisi dietetici ({dietWarnings.length})
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
            {dietWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Distribuzione pasti per giorno
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <th style={th}>Settimana</th>
            <th style={th}>Giorno</th>
            <th style={th}>Pasti</th>
            <th style={th}>Varianti</th>
            <th style={th}>Digiuno</th>
            <th style={th}>Kcal</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: draft.unique_weeks }, (_, i) => i + 1).flatMap((w) =>
            [1, 2, 3, 4, 5, 6, 7].map((d) => {
              const dayMeals = draft.meals.filter(
                (m) => (m.week_of_plan ?? 1) === w && m.day_of_week === d
              );
              if (dayMeals.length === 0) return null;
              const variants = new Set(
                dayMeals.map((m) => m.variant_group).filter((v): v is number => v !== null)
              ).size;
              const fasting = dayMeals.filter((m) => m.is_fasting === 1).length;
              const kcal = dayMeals.reduce(
                (sum, m) => (m.is_fasting === 1 ? sum : sum + m.items.reduce((a, it) => a + (it.calories ?? 0), 0)),
                0
              );
              return (
                <tr key={`${w}-${d}`}>
                  <td style={td}>{w}</td>
                  <td style={td}>{DAY_LABELS[d - 1]}</td>
                  <td style={td}>{dayMeals.length}</td>
                  <td style={td}>{variants}</td>
                  <td style={td}>{fasting}</td>
                  <td style={td}>{kcal > 0 ? `~${kcal}` : '—'}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </section>
  );
}

// ============================================================================
// Modale clone multi-paziente
// ============================================================================
function CloneToManyModal({
  patients,
  selected,
  onToggle,
  onCancel,
  onConfirm,
  loading,
  sourceName,
  savedPlanId,
}: {
  patients: Array<{ id: string; name: string; human_id: string | null }>;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  sourceName: string;
  savedPlanId: string | null;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => !loading && onCancel()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 14,
          padding: 22,
          maxWidth: 520,
          width: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.32)',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        <h3 style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
          Piano "{sourceName}" creato {savedPlanId ? `(ID: ${savedPlanId.slice(0, 8)})` : ''}
        </h3>
        <p style={{ color: 'var(--mute)', fontSize: 13, lineHeight: 1.55, margin: '0 0 14px' }}>
          Vuoi applicare lo stesso piano (clone deep) anche ad altri pazienti?
          Seleziona i target oppure salta.
        </p>

        <div
          style={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid var(--line)',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {patients.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--mute)' }}>Nessun altro paziente.</div>
          ) : (
            patients.map((p) => {
              const checked = selected.has(p.id);
              return (
                <label
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    borderBottom: '1px solid var(--line-3)',
                    cursor: 'pointer',
                    background: checked ? 'var(--green-soft)' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(p.id)}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--mute)', fontFamily: 'var(--mono)' }}>
                      {p.human_id || '—'}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              padding: '9px 14px',
              borderRadius: 8,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Salta
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              border: 0,
              background: 'var(--green-deep)',
              color: '#fff',
              padding: '9px 14px',
              borderRadius: 8,
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Cloning…' : `Clona a ${selected.size} pazienti`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers UI
// ============================================================================
function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function ReviewKpi({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 12,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--display)',
          fontSize: 16,
          fontWeight: 700,
          color: 'var(--ink)',
          marginTop: 4,
          letterSpacing: -0.4,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  boxSizing: 'border-box',
};

const th: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontFamily: 'var(--mono)',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--mute)',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--line-3)',
};

const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--line-3)',
  fontSize: 13,
};
